import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Star, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import PricingPage from '../pages/pricingpage';
import { useNavigate } from 'react-router-dom';



const ServiceBlogTemplate = ({
    location,
    service,
    serviceDescription,
    faq = [],
    caseStudies = [],
    testimonials = [],
    processSteps = [],
    benefits = []
}) => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    const navigate = useNavigate();
    return (
        <article className="min-h-screen bg-white">
            {/* Hero Section - Centered Design */}
            <motion.header
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-gray-800 py-24 text-center relative overflow-hidden"
            >
                <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.8 }}
                    className="container mx-auto px-4"
                >
                    <span className="bg-white text-black px-4 py-1 rounded-full text-sm font-medium inline-block mb-6">
                        Top Rated {service} Development Company  {location}
                    </span>
                    <h1 className="text-5xl md:text-7xl font-bold text-blue-600 mb-6 max-w-4xl mx-auto">
                        Transform Your Business With Expert {service} Solutions
                    </h1>
                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                        Trusted by 100+ businesses in {location} for delivering innovative {service} solutions
                    </p>
                    <div className="flex justify-center gap-4">
                        <button onClick={() => navigate('/contact')} className="bg-blue-500 text-black px-8 py-4 rounded-lg font-semibold hover:bg-blue-600 transition-colors">
                            Get Free Consultation
                        </button>
                        <button     onClick={() => navigate('/projects')} className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-black transition-colors">
                            View Portfolio
                        </button>
                    </div>
                </motion.div>
            </motion.header>

            {/* Trust Indicators */}
            <section className="bg-gray-50 py-12">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <p className="text-3xl font-bold text-black">100+</p>
                            <p className="text-gray-600">Projects Delivered</p>
                        </div>
                        <div>
                            <p className="text-3xl font-bold text-black">99%</p>
                            <p className="text-gray-600">Client Satisfaction</p>
                        </div>
                        <div>
                            <p className="text-3xl font-bold text-black">50+</p>
                            <p className="text-gray-600">Expert Developers</p>
                        </div>
                        <div>
                            <p className="text-3xl font-bold text-black">10+</p>
                            <p className="text-gray-600">Years of Industry Experience</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-16">
                {/* Introduction */}
                <motion.section
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold mb-6">
                        Premier {service} Development Services in {location}
                    </h2>
                    <div className="prose prose-lg max-w-none">
                        <p>{serviceDescription}</p>
                    </div>
                </motion.section>

                {/* Process Steps */}
                <motion.section
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold mb-8">Our Development Process</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <div key={index} className="bg-gray-50 p-6 rounded-xl relative">
                                <div className="bg-blue-500 w-8 h-8 rounded-full flex items-center justify-center text-black font-bold mb-4">
                                    {index + 1}
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                                <p className="text-gray-600">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </motion.section>

                <PricingPage />

                {/* Testimonials */}
                {/* <motion.section
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold mb-8">Client Testimonials</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                                <div className="flex items-center gap-2 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="text-blue-500" size={20} fill="currentColor" />
                                    ))}
                                </div>
                                <p className="text-gray-600 mb-4">{testimonial.content}</p>
                                <div className="flex items-center gap-4">
                                    <div>
                                        <p className="font-semibold">{testimonial.name}</p>
                                        <p className="text-gray-500 text-sm">{testimonial.company}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.section> */}

                {/* CTA Section */}
                <motion.section
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl p-12 text-center"
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Build Your {service} Solution?
                    </h2>
                    <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                        Get a free consultation and let's discuss how we can help your business grow with a custom {service} solution.
                    </p>
                    <button className="bg-blue-500 text-black px-8 py-4 rounded-lg font-semibold hover:bg-blue-600 transition-colors">
                        Schedule Free Consultation
                    </button>
                </motion.section>
            </main>
        </article>
    );
};

export default ServiceBlogTemplate;